'use client'

import { useState } from 'react'

import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'

import WhatsAppIcon from '@mui/icons-material/WhatsApp'
import classnames from 'classnames'
import { GoogleMap, LoadScript, Marker } from '@react-google-maps/api'

import { Box, Card, CircularProgress, IconButton, Divider } from '@mui/material'

import AlternateEmailIcon from '@mui/icons-material/AlternateEmail'
import PhoneIcon from '@mui/icons-material/Phone'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import frontCommonStyles from '@views/styles.module.css'
import { frontLayoutClasses } from '@layouts/utils/layoutClasses'

import LogoWhite from '../shared/LogoWhite'

const location = {
  lat: -7.3402946,
  lng: 108.2138089
}

const Footer = () => {
  const [isLoaded, setIsLoaded] = useState(false)

  const handleMapLoad = () => {
    setIsLoaded(true)
  }

  const handleWhatsAppClick = () => {
    const phoneNumber = '6282118546605'
    const message = 'Halo, saya ingin bertanya tentang layanan TMC Eternal Home'
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`

    window.open(url, '_blank')
  }

  const handlePhoneClick = () => {
    window.open('tel:+6282118546605', '_self')
  }

  const handleEmailClick = () => {
    window.open('mailto:eternalhometmc@gmail.com', '_self')
  }

  const mapOptions = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'cooperative',
    styles: [
      {
        featureType: 'water',
        elementType: 'geometry',
        stylers: [{ color: '#4A90E2' }]
      },
      {
        featureType: 'landscape',
        elementType: 'geometry',
        stylers: [{ color: '#f5f5f5' }]
      },
      {
        featureType: 'road',
        elementType: 'geometry',
        stylers: [{ color: '#ffffff' }]
      },
      {
        featureType: 'poi',
        elementType: 'geometry',
        stylers: [{ color: '#e8e8e8' }]
      }
    ]
  }

  return (
    <>
      <footer
        className={`${frontLayoutClasses.footer} bg-gradient-to-br from-[#1B5F8A] via-[#2887bf] to-[#5DAEDA] relative overflow-hidden`}
      >
        {/* Background Pattern */}
        <div className='absolute inset-0 opacity-10'>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.3\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
        </div>
        <div className='relative z-10'>
          {/* Main Content Section */}
          <div className={classnames('py-12 lg:py-16 text-white', frontCommonStyles.layoutSpacing)}>
            <Grid container spacing={4} alignItems='stretch'>
              {/* Company Information */}
              <Grid item xs={12} lg={5}>
                <div className='flex flex-col gap-4 h-full'>
                  <div className='mb-2'>
                    <LogoWhite color='var(--mui-palette-common-white)' />
                  </div>

                  <Typography variant='h5' className='font-bold text-white mb-2'>
                    TMC Eternal Home
                  </Typography>

                  <Typography className='text-white/90 leading-relaxed mb-4 text-sm lg:text-base'>
                    Memberikan layanan terbaik dengan penuh dedikasi dan profesionalisme untuk keluarga Indonesia.
                  </Typography>

                  {/* Contact Information */}
                  <div className='flex flex-col gap-3'>
                    <Typography variant='h6' className='font-semibold text-white mb-2 flex items-center gap-2'>
                      <LocationOnIcon fontSize='small' />
                      Kontak & Lokasi
                    </Typography>

                    <div className='space-y-2'>
                      <Typography className='flex items-start gap-3 text-white/90 text-sm hover:text-white transition-colors'>
                        <LocationOnIcon fontSize='small' className='mt-0.5 flex-shrink-0' />
                        <span>
                          JL. KH. Lukman Hakim, RT.02/RW.10, Tuguraja, Kec.Cihideung, Kota Tasikmalaya, Jawa Barat 46125
                        </span>
                      </Typography>

                      <div
                        className='flex items-center gap-3 text-white/90 text-sm hover:text-white transition-colors cursor-pointer'
                        onClick={handlePhoneClick}
                      >
                        <PhoneIcon fontSize='small' />
                        <span>+62 821-1854-6605</span>
                      </div>

                      <div
                        className='flex items-center gap-3 text-white/90 text-sm hover:text-white transition-colors cursor-pointer'
                        onClick={handleWhatsAppClick}
                      >
                        <WhatsAppIcon fontSize='small' />
                        <span>+62 821-1854-6605 (WhatsApp)</span>
                      </div>

                      <div
                        className='flex items-center gap-3 text-white/90 text-sm hover:text-white transition-colors cursor-pointer'
                        onClick={handleEmailClick}
                      >
                        <AlternateEmailIcon fontSize='small' />
                        <span>eternalhometmc@gmail.com</span>
                      </div>

                      <div className='flex items-center gap-3 text-white/90 text-sm'>
                        <AccessTimeIcon fontSize='small' />
                        <span>24 Jam Siaga Melayani</span>
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>

              {/* Map Section */}
              <Grid item xs={12} lg={7}>
                <div className='h-full'>
                  <Typography variant='h6' className='font-semibold text-white mb-4 flex items-center gap-2'>
                    <LocationOnIcon fontSize='small' />
                    Lokasi Kami
                  </Typography>

                  <Card
                    elevation={12}
                    className='rounded-2xl shadow-2xl bg-white overflow-hidden transition-transform hover:scale-[1.02] duration-300'
                    sx={{
                      height: { xs: 250, sm: 300, lg: 350 },
                      position: 'relative'
                    }}
                  >
                    <LoadScript
                      googleMapsApiKey={process.env.NEXT_PUBLIC_GOOGLE_API_MAPS as string}
                      onLoad={handleMapLoad}
                    >
                      {!isLoaded ? (
                        <Box className='flex flex-col justify-center items-center h-full bg-gray-50'>
                          <CircularProgress style={{ color: '#2887bf' }} size={40} />
                          <Typography variant='caption' className='mt-2 text-gray-600'>
                            Memuat peta...
                          </Typography>
                        </Box>
                      ) : (
                        <GoogleMap
                          key={location.lat + location.lng}
                          mapContainerStyle={{ height: '100%', width: '100%' }}
                          center={location}
                          zoom={18}
                          options={mapOptions}
                        >
                          <Marker position={location} title='TMC Eternal Home' />
                        </GoogleMap>
                      )}
                    </LoadScript>
                  </Card>
                </div>
              </Grid>
            </Grid>
          </div>

          {/* Divider */}
          <Divider className='border-white/20' />

          {/* Bottom Copyright Section */}
          <div className='bg-black/10 backdrop-blur-sm'>
            <div
              className={classnames(
                'flex flex-col sm:flex-row items-center justify-between gap-4 py-6 text-center sm:text-left',
                frontCommonStyles.layoutSpacing
              )}
            >
              <div className='flex flex-col sm:flex-row items-center gap-2'>
                <Typography variant='body2' className='text-white/90 font-medium'>
                  © {new Date().getFullYear()} TMC Eternal Home.
                </Typography>
                <Typography variant='body2' className='text-white/70 text-sm'>
                  Hak cipta dilindungi.
                </Typography>
              </div>

              <div className='flex items-center gap-2'>
                <Typography variant='body2' className='text-white/70 text-sm'>
                  Dibangun dengan ❤️ oleh
                </Typography>
                <Typography variant='body2' className='text-white font-semibold text-sm'>
                  IT SAMCO
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  )
}

export default Footer
